import {
  AlloggiamentoFiltroDto,
  FiltroDto,
  useGetAlloggiamentoFiltro,
} from "@/api";
import Loading from "@/components/Loading";
import Button from "@/elements/Button";
import Table from "@/elements/Table";
import { portaleConfig } from "@/portaliConfig";
import { getFiltroTypeHelper } from "@/utils/filtroTypeUtils";
import { Box, Card, Grid, Typography } from "@mui/material";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import moment from "moment";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

const columnHelper = createColumnHelper<FiltroDto>();

export function AlloggiamentoFiltro({
  alloggiamentoFiltro,
}: {
  alloggiamentoFiltro: AlloggiamentoFiltroDto;
}) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const hasFiltro = !!alloggiamentoFiltro.filtroAttuale?.modelloFiltro;

  const columns = useMemo<ColumnDef<FiltroDto, any>[]>(() => {
    return [
      columnHelper.accessor(
        (row) => moment(row.montatoIl).format("DD/MM/YYYY"),
        {
          header: t("alloggiamentoFiltroPage.colonne.montatoIl"),
        }
      ),

      columnHelper.accessor(
        (row) => `${row.modelloFiltro?.nome} - ${row.modelloFiltro?.marca}`,
        {
          header: t("alloggiamentoFiltroPage.colonne.modelloFiltro"),
        }
      ),

      columnHelper.accessor(
        (row) =>
          row.daSostituireDopoIl
            ? moment(row.daSostituireDopoIl).format("DD/MM/YYYY")
            : "",
        {
          header: t("alloggiamentoFiltroPage.colonne.daSostituireDopoIl"),
        }
      ),
    ];
  }, [t]);

  if (alloggiamentoFiltro) {
    return (
      <>
        <Grid container columnSpacing={1} rowSpacing={2} mt={2}>
          <Grid item sm={12} lg={6} sx={{ width: "100%" }}>
            <Card>
              <Box display="flex" flexDirection={"column"} gap={2} p={1}>
                <Box
                  display="flex"
                  flexDirection={"column"}
                  alignItems={"center"}
                >
                  <Typography variant="h4">
                    {t("alloggiamentoFiltroPage.titoli.centrale")}
                    <span style={{ fontWeight: "bold", marginLeft: 4 }}>
                      {alloggiamentoFiltro.nodo?.nome ?? ""}
                    </span>
                  </Typography>
                  <Typography variant="h4">
                    {t("alloggiamentoFiltroPage.titoli.alloggiamento")}
                    <span style={{ fontWeight: "bold", marginLeft: 4 }}>
                      {alloggiamentoFiltro.nome}
                    </span>
                  </Typography>
                </Box>
                <Box
                  display={"grid"}
                  width={"100%"}
                  gap={"1px"}
                  gridTemplateColumns={"1fr 1fr"}
                >
                  {/* <Typography
                    variant="h5"
                    p={1}
                    sx={{ backgroundColor: "#fff" }}
                  >
                    Alloggiamento:
                  </Typography>
                  <Typography variant="h5" p={1} fontWeight={"regular"}>
                    <span style={{ fontWeight: "bold" }}>
                      {alloggiamentoFiltro.nome}{" "}
                    </span>
                    (
                    {getFiltroTypeHelper(alloggiamentoFiltro.filtroType)?.label}
                    )
                  </Typography> */}

                  <Typography
                    variant="h5"
                    p={1}
                    sx={{ backgroundColor: "#fff" }}
                  >
                    {t("alloggiamentoFiltroPage.campi.tipo")}
                  </Typography>
                  <Typography variant="h5" p={1} fontWeight={"regular"}>
                    {getFiltroTypeHelper(alloggiamentoFiltro.filtroType, i18n.language)?.label}
                  </Typography>

                  <Typography variant="h5" p={1}>
                    {t("alloggiamentoFiltroPage.campi.filtro")}
                  </Typography>
                  <Typography
                    variant="h5"
                    p={1}
                    fontWeight={"regular"}
                    fontStyle={
                      alloggiamentoFiltro.filtroAttuale?.modelloFiltro
                        ? "normal"
                        : "italic"
                    }
                  >
                    {hasFiltro
                      ? `${alloggiamentoFiltro?.filtroAttuale?.modelloFiltro?.nome} - ${alloggiamentoFiltro?.filtroAttuale?.modelloFiltro?.marca}`
                      : t("alloggiamentoFiltroPage.messaggi.nessunFiltro")}
                  </Typography>

                  <Typography variant="h5" p={1}>
                    {t("alloggiamentoFiltroPage.campi.montatoIl")}
                  </Typography>
                  <Typography variant="h5" p={1} fontWeight={"regular"}>
                    {hasFiltro
                      ? moment(
                          alloggiamentoFiltro.filtroAttuale?.montatoIl
                        ).format("L")
                      : "-"}
                  </Typography>

                  <Typography variant="h5" p={1}>
                    {t("alloggiamentoFiltroPage.campi.daSostituireDopoIl")}
                  </Typography>
                  <Typography variant="h5" p={1} fontWeight={"regular"}>
                    {hasFiltro
                      ? moment(
                          alloggiamentoFiltro.filtroAttuale?.daSostituireDopoIl
                        ).format("L")
                      : "-"}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>

          <Grid item sm={12} lg={6} sx={{ width: "100%" }}>
            <Card>
              <Box display="flex" flexDirection={"column"} gap={2} p={1}>
                <Typography variant="h4" fontWeight={"bold"}>
                  {t("alloggiamentoFiltroPage.titoli.storico")}
                </Typography>

                <Table
                  columns={columns}
                  data={alloggiamentoFiltro.storicoFiltri}
                  noRowsText={t("alloggiamentoFiltroPage.messaggi.storicoAssente")}
                  mt={1}
                />
              </Box>
            </Card>
          </Grid>
        </Grid>

        {alloggiamentoFiltro.nodo && (
          <Box
            display="flex"
            flexDirection={"column"}
            alignItems={"center"}
            sx={{ my: 4 }}
          >
            <Button
              size="large"
              onClick={() => {
                navigate(
                  `/login?redirect=/nodi/${alloggiamentoFiltro.nodo?.id}`
                );
              }}
            >
              {t("alloggiamentoFiltroPage.azioni.apriCentrale")}
            </Button>
          </Box>
        )}
      </>
    );
  } else {
    return <></>;
  }
}

export default function AlloggiamentoFiltroPage() {
  const params = useParams();
  const tokenCodiceQR = params.id ?? "";
  const { t } = useTranslation();

  const { isLoading, error, data } = useGetAlloggiamentoFiltro(tokenCodiceQR);

  if (isLoading) {
    return <Loading />;
  }
  if (error) {
    return (
      <Box m={4}>
        <Typography color="secondary">
          {t("alloggiamentoFiltroPage.errori.qrCode")}
        </Typography>
      </Box>
    );
  }

  return <AlloggiamentoFiltro alloggiamentoFiltro={data.data} />;
}
